import { DEFAULT_NAV_ITEMS, type DefaultNavItem } from "@healthos/ui/shell";
import { useEffect } from "react";
import { useTheme } from "./theme";

/**
 * Each destination owns one hue (its nav icon's), and the page wears it: the
 * emphasised word in the header, the eyebrow dot, the active tab. Teal stays the
 * colour of actions and progress instead of every heading on every page.
 *
 * Dark only. On light every page keeps the house teal, which is what each
 * `var(--page-accent, <teal>)` falls back to when the property is unset.
 */
const DESTINATIONS: readonly DefaultNavItem[] = DEFAULT_NAV_ITEMS.flatMap((item) => [
  item,
  ...(item.children ?? []),
]);

/** Most specific path first, so /explore/collection beats /explore. */
const BY_LENGTH = [...DESTINATIONS].sort((a, b) => b.path.length - a.path.length);

/** The `--color-vivid-*` value for a path, or null where no destination owns it. */
export function pageAccentFor(pathname: string): string | null {
  if (pathname === "/" || pathname === "/today") {
    return DESTINATIONS.find((item) => item.id === "ambient")?.tone ?? null;
  }
  const match = BY_LENGTH.find(
    (item) => item.path !== "/" && (pathname === item.path || pathname.startsWith(`${item.path}/`)),
  );
  return match?.tone ?? null;
}

/**
 * Sets `--page-accent` on the root for the current route in dark; removes it
 * on light and on routes no destination owns.
 */
export function usePageAccent(pathname: string): void {
  const { resolved } = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    const accent = resolved === "dark" ? pageAccentFor(pathname) : null;
    if (accent) root.style.setProperty("--page-accent", accent);
    else root.style.removeProperty("--page-accent");
  }, [pathname, resolved]);
}
